import { useGetSingleApplicantsQuery } from '@/app/features/recruitment/job-applicants/job-applicants.api';
import IMAGES from '@/assets/images';
import CustomDrawer from '@/components/CustomDrawer';
import CustomImage from '@/components/CustomImage';
import type { CustomModalProps } from '@/components/CustomModal';
import { Call02Icon, MailIcon, PaperclipIcon } from '@/icons';
import { formatDate } from '@/utils';
import { Flex, Progress, Skeleton } from 'antd';

type Props = CustomModalProps & { id?: string };

export default function ApplicantsDetails({ id, open, handleClose }: Props) {
  const { data: applicantRes, isFetching } = useGetSingleApplicantsQuery(
    id as string,
    { skip: !id || !open },
  );

  const data = applicantRes?.data;

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={handleClose}
      title={'Applicants Details'}
    >
      {isFetching ? (
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between border-b border-gray-700 pb-4">
            <div className="flex items-center gap-4">
              <Skeleton.Avatar active size={56} shape="circle" />
              <div className="flex flex-col gap-2">
                <Skeleton.Input active size="small" style={{ width: 150 }} />
                <Skeleton.Input active size="small" style={{ width: 200 }} />
                <Skeleton.Input active size="small" style={{ width: 120 }} />
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <Skeleton.Input active size="small" style={{ width: 150 }} />
              <Skeleton.Input active size="small" style={{ width: 180 }} />
            </div>
          </div>

          {[1, 2, 3, 4].map((i) => (
            <Flex key={i} gap={8} justify="space-between">
              <Skeleton.Input active size="small" style={{ width: 100 }} />
              <Skeleton.Input active size="small" style={{ width: 150 }} />
            </Flex>
          ))}

          <div className="mt-4">
            <Skeleton active paragraph={{ rows: 4 }} />
          </div>

          <div className="flex justify-end mt-4">
            <Skeleton.Button active size="large" style={{ width: 100 }} />
          </div>
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between border-b border-gray-700 pb-4">
            <div className="flex items-center gap-4">
              <CustomImage
                size={56}
                className="rounded-full object-cover"
                src={data?.photo || IMAGES.avatar}
              />
              <div>
                <div className="justify-start text-white text-xl font-medium">
                  {data?.name || 'N/A'}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <MailIcon size={14} className="text-white" />
                  <div className="justify-start text-white text-sm font-normal">
                    {data?.email || 'n/a'}
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <Call02Icon size={14} className="text-white" />
                  <div className="justify-start text-white text-sm font-normal">
                    {data?.phone || 'n/a'}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-end gap-2">
              <p className="text-sm text-gray-400">
                Department :{' '}
                <span className="text-gray-300">
                  {/* @ts-expect-error - department_name might exist in API response */}
                  {data?.job?.department_name || 'n/a'}
                </span>
              </p>

              <p className="text-sm text-gray-400">
                Assigned Evaluator :{' '}
                <span className="text-gray-300">
                  {/* @ts-expect-error - evaluator_name might exist in API response */}
                  {data?.job?.evaluator_name || 'n/a'}
                </span>
              </p>
            </div>
          </div>

          <Flex gap={8} justify="space-between" className="mt-3">
            <div className="justify-start text-white text-base font-light">
              Applied Job
            </div>

            <div className="text-right justify-start text-white text-lg font-medium">
              {data?.job?.job_title || 'n/a'}
            </div>
          </Flex>

          <Flex gap={8} justify="space-between" className="mt-3">
            <div className="justify-start text-white text-base font-light">
              Applied Role
            </div>

            <div className="text-right justify-start text-white text-lg font-medium">
              {(data?.job as any)?.position_name || 'n/a'}
            </div>
          </Flex>

          <Flex gap={8} justify="space-between" className="mt-3">
            <div className="justify-start text-white text-base font-light">
              Applied On
            </div>

            <div className="text-right justify-start text-white text-lg font-medium">
              {data?.applied_at ? formatDate(data?.applied_at) : 'n/a'}
            </div>
          </Flex>

          <Flex gap={8} justify="space-between" className="mt-3">
            <div className="justify-start text-white text-base font-light">
              CV
            </div>

            <a
              href={data?.cv_link}
              target="_blank"
              rel="noreferrer"
              className="h-5 p-[5px] bg-zinc-300 rounded inline-flex justify-center items-center gap-[3px]"
            >
              <div className="w-3.5 h-3.5 relative">
                <PaperclipIcon size={14} className="text-sky-600" />
              </div>
              <div className="justify-start text-neutral-700 text-sm font-normal">
                {data?.cv_link ? 'View CV' : 'n/a'}
              </div>
            </a>
          </Flex>

          <Flex gap={8} justify="space-between" className="mt-3">
            <div>
              <div className="justify-start text-white text-base font-light">
                Current Stage
              </div>

              <Progress
                percent={
                  data?.stage?.toLowerCase() === 'screening'
                    ? 25
                    : data?.stage?.toLowerCase() === 'assessment'
                      ? 50
                      : data?.stage?.toLowerCase() === 'interview'
                        ? 75
                        : data?.stage?.toLowerCase() === 'offer'
                          ? 100
                          : 0
                }
                showInfo={false}
                strokeColor="#14b8a6" // teal-500
                trailColor="#d4d4d8" // zinc-300
                strokeWidth={12}
                className="w-96 mt-1"
              />
            </div>

            <div className="text-right justify-start text-white text-lg font-medium capitalize">
              {data?.stage || 'n/a'}
            </div>
          </Flex>

          {/* <Card
            className="dark:bg-theme-slate-800 border dark:border-theme-gray-700 rounded-xl text-white shadow-md mt-5"
            bodyStyle={{ padding: 0 }}
          >
            <Collapse
              defaultActiveKey={['1']}
              ghost
              expandIconPosition="end"
              expandIcon={({ isActive }) => (
                <ChevronDownIcon
                  size={20}
                  className={`!text-[#14b8a6] transition-transform duration-200 ${
                    isActive ? 'rotate-180' : ''
                  }`}
                />
              )}
              items={[
                {
                  key: '1',
                  label: (
                    <Space size={8}>
                      <div className="text-white text-xl font-bold">
                        CV Analysis Report
                      </div>
                      <div className="p-[5px] rounded outline outline-[0.5px] outline-teal-500">
                        <div className="text-teal-500 text-sm font-bold">
                          50%
                        </div>
                      </div>
                    </Space>
                  ),
                  children: (
                    <div className="p-[22px]">
                      <Space size={8}>
                        <div className="text-white text-sm font-normal">
                          AI Procession
                        </div>
                        <Switch className="custom-switch" />
                      </Space>

                      <div className="mt-5 grid grid-cols-4 gap-5">
                        {[
                          { value: '0/100', label: 'ATS Score' },
                          { value: '0%', label: 'Skill' },
                          { value: '0%', label: 'Years Of Experience' },
                          { value: 'Reject', label: 'Recommendation' },
                        ].map((item, i) => (
                          <div
                            key={i}
                            className="p-6 bg-slate-800 rounded-lg outline outline-1 outline-gray-700"
                          >
                            <div className="text-white text-xl font-bold">
                              {item.value}
                            </div>
                            <div className="text-white text-sm font-normal capitalize">
                              {item.label}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div
                        className="p-6 bg-slate-800 rounded-lg outline outline-1 outline-offset-[-1px]
             outline-gray-700  gap-2.5 mt-5"
                      >
                        <div className="justify-start text-white text-base font-normal">
                          Overall Score 10 out of 100
                        </div>
                        <Progress
                          percent={10}
                          showInfo={false}
                          strokeColor="#14b8a6" // teal-500
                          trailColor="#d4d4d8" // zinc-300
                          strokeWidth={12}
                          className="w-96 mt-1"
                        />
                        <div className="justify-start text-white text-sm font-light capitalize mt-2">
                          The provided CV is entirely empty, containing no
                          information regarding the candidate's skills,
                          experience, <br />
                          or education. Consequently, it is impossible to
                          evaluate their suitability for the Salesforce
                          Developer role against
                          <br /> the job requirements. A rejection is
                          recommended until a complete and informative CV is
                          submitted.
                        </div>
                      </div>

                      <div
                        className="p-6 bg-slate-800 rounded-lg outline outline-1 outline-offset-[-1px]
             outline-gray-700  gap-2.5 mt-5"
                      >
                        <div className="justify-start text-white text-base font-normal">
                          Skill Relevance 60 out of 100
                        </div>
                        <Progress
                          percent={60}
                          showInfo={false}
                          strokeColor="#14b8a6" // teal-500
                          trailColor="#d4d4d8" // zinc-300
                          strokeWidth={12}
                          className="w-96 mt-1"
                        />
                        <div className="justify-start text-white text-sm font-light capitalize mt-2">
                          The CV is empty, therefore no skills could be
                          identified or matched against the required skill set
                          for the Salesforce Developer role.
                        </div>
                      </div>

                      <div
                        className="p-6 bg-slate-800 rounded-lg outline outline-1 outline-offset-[-1px]
             outline-gray-700  gap-2.5 mt-5"
                      >
                        <div className="justify-start text-white text-base font-normal">
                          Experience Alignment 40 out of 100
                        </div>
                        <Progress
                          percent={40}
                          showInfo={false}
                          strokeColor="#14b8a6" // teal-500
                          trailColor="#d4d4d8" // zinc-300
                          strokeWidth={12}
                          className="w-96 mt-1"
                        />
                        <div className="justify-start text-white text-sm font-light capitalize mt-2">
                          The CV is empty, providing no evidence of any work
                          experience, let alone the required 7 years of
                          Salesforce Development experience.
                        </div>
                      </div>

                      <div
                        className="p-6 bg-slate-800 rounded-lg outline outline-1 outline-offset-[-1px]
             outline-gray-700  gap-2.5 mt-5"
                      >
                        <div className="justify-start text-white text-base font-normal">
                          Education Fit 30 out of 100
                        </div>
                        <Progress
                          percent={30}
                          showInfo={false}
                          strokeColor="#14b8a6" // teal-500
                          trailColor="#d4d4d8" // zinc-300
                          strokeWidth={12}
                          className="w-96 mt-1"
                        />
                        <div className="justify-start text-white text-sm font-light capitalize mt-2">
                          The CV is empty, so no educational background could be
                          assessed for relevance to the role.
                        </div>
                      </div>

                      <div
                        className="p-6 bg-slate-800 rounded-lg outline outline-1 outline-offset-[-1px]
             outline-gray-700  gap-2.5 mt-5"
                      >
                        <div className="justify-start text-white text-base font-normal">
                          Key Strength
                        </div>

                        <div className="justify-start text-white text-sm font-light capitalize mt-2">
                          N/A
                        </div>
                      </div>

                      <div
                        className="p-6 bg-slate-800 rounded-lg outline outline-1 outline-offset-[-1px]
             outline-gray-700  gap-2.5 mt-5"
                      >
                        <div className="justify-start text-white text-base font-normal">
                          Area For Improvement
                        </div>

                        <div className="justify-start text-white text-sm font-light capitalize mt-2">
                          N/A
                        </div>
                      </div>

                      <div
                        className="p-6 bg-slate-800 rounded-lg outline outline-1 outline-offset-[-1px]
             outline-gray-700  gap-2.5 mt-5"
                      >
                        <div className="justify-start text-white text-base font-normal">
                          Recommendation
                        </div>

                        <div className="justify-start text-white text-sm font-light capitalize mt-2">
                          N/A
                        </div>
                      </div>
                    </div>
                  ),
                },
              ]}
            />
          </Card> */}

          {/* <div className="flex justify-end gap-4 mt-5">
            <Button size="large" className="btn-primary min-w-20">
              Shortlist
            </Button>
          </div> */}
        </div>
      )}
    </CustomDrawer>
  );
}
